import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getClientApi, updateClientApi } from '../api/clientApi';
import api from '../api/axios';
import { uploadFiles, getFilesByLink } from '../api/clientApi';
import { useAuth } from '../context/AuthContext';
import QuotesTab from './QuotesTab';
import FinancialsTab from './FinancialsTab';
import OrdersTab from './OrdersTab';
import InvoicesTab from './InvoicesTab';
import ClientInventoryTab from './ClientInventoryTab';
import './ClientDetail.css';



// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const getInitials = (name='') => name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
const avatarColor = (name='') => {
  const colors = ['#10B981','#3B82F6','#8B5CF6','#F59E0B','#EF4444','#6366F1','#EC4899'];
  return colors[(name.charCodeAt(0)||0) % colors.length];
};
const FILE_BASE_URL =
  (process.env.REACT_APP_API_URL
    ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/i, '')
    : null) || 'http://localhost:5000';
const resolveFileUrl = (url = '', { clientId } = {}) => {
  if (!url) return '';
  // backend sometimes returns absolute URLs; don't double-prefix
  if (/^https?:\/\//i.test(url)) return url;
  // Backward-compat: older records were saved as `/uploads/client/<type>/<filename>`
  // but the real path is `/uploads/client-files/<clientId>/<type>/<filename>`.
  if (clientId) {
    const m = url.match(/^\/uploads\/client\/([^/]+)\/([^/]+)$/i);
    if (m) {
      const [, type, filename] = m;
      return `${FILE_BASE_URL}/uploads/client-files/${clientId}/${type}/${filename}`;
    }
  }
  if (url.startsWith('/')) return `${FILE_BASE_URL}${url}`;
  return `${FILE_BASE_URL}/${url}`;
};

// ── Tab definitions ───────────────────────────────────────────────────────────
// locked = only available when client is converted (toggled ON)
const ALL_TABS = [
  { id:'files',            label:'Files',            locked:false, icon:'file' },
  { id:'moodboard',        label:'Moodboard',        locked:false, icon:'image' },
  { id:'notes',            label:'Notes',            locked:false, icon:'note' },
  { id:'tasks',            label:'Tasks',            locked:false, icon:'check' },
  { id:'quotes',           label:'Quotes',           locked:false, icon:'doc' },
  { id:'orders',     label:'Orders',    locked:false, icon:'doc' },
  { id:'invoices', label:'Invoices', locked:false, icon:'doc' },
  { id:'purchase_request', label:'Purchase Request', locked:true,  icon:'doc' },
  { id:'inventory',        label:'Inventory',        locked:false, icon:'box' },
  { id:'client_progress',  label:'Client Progress',  locked:true,  icon:'chart' },
  { id:'milestones',       label:'Milestones',       locked:true,  icon:'flag' },
  { id:'financials', label:'Financials',locked:false, icon:'money' },
  { id:'checklists',       label:'Checklists',       locked:true,  icon:'list' },
  { id:'details',          label:'Details',          locked:false, icon:'info' },
];

const VISIBLE_CLIENT_TABS = ALL_TABS.filter(
  (t) => !['milestones', 'activities', 'manpower', 'vendor_milestone', 'studioai'].includes(t.id)
);

function getEffectiveClientTab(raw, isConverted) {
  const t = raw || 'files';
  const allowed = new Set(VISIBLE_CLIENT_TABS.map((v) => v.id));
  if (!allowed.has(t)) return 'files';
  const meta = VISIBLE_CLIENT_TABS.find((v) => v.id === t);
  if (meta?.locked && !isConverted) return 'files';
  return t;
}

const FILE_CATS = [
  { key: 'recce', label: 'Recce' },
  { key: '2d-design', label: '2D Design' },
  { key: '3d-design', label: '3D Design' },
];

// ── SVG Icons ─────────────────────────────────────────────────────────────────
const Icon = ({ name, size=13 }) => {
  const icons = {
    file:    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>,
    image:   <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></>,
    note:    <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
    check:   <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>,
    doc:     <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    box:     <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></>,
    chart:   <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    flag:    <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
    money:   <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
    list:    <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
    info:    <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
    back:    <><path d="M19 12H5M12 19l-7-7 7-7"/></>,
    edit:    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    upload:  <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
    refresh: <><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></>,
    reply:   <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
    forward: <><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 0 0 4 4h12"/></>,
    download:<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    more:    <><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></>,
    phone:   <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>,
    user:    <><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></>,
    grid:    <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>,
    rows:    <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {icons[name]}
    </svg>
  );
};

// ── Info Tooltip ──────────────────────────────────────────────────────────────
function InfoTooltip({ client }) {
  return (
    <div style={{ position:'absolute', top:36, left:0, background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.12)', zIndex:300, minWidth:300, padding:'16px 20px' }}>
      {[
        ['Client Name',     client.name],
        ['Stage',           client.status,  true],
        ['Phone',           client.phone],
        ['Project Address', [client.address?.city, client.address?.state].filter(Boolean).join(', ') || '—'],
        ['Client GST',      '—'],
        ['Legal Name',      client.company || '—'],
      ].map(([label, value, isBadge]) => (
        <div key={label} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, fontSize:13 }}>
          <span style={{ fontWeight:700, color:'#374151', minWidth:130 }}>{label}:</span>
          <span style={{ color:'#6B7280' }}>{value || '—'}</span>
          {isBadge && value && (
            <span style={{ background:'#3B82F6', color:'#fff', padding:'1px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>Sales</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Rich Text Toolbar ─────────────────────────────────────────────────────────
function RichToolbar({ onFormat }) {
  const btns = [
    { label:'B',  cmd:'bold',           style:{ fontWeight:700 } },
    { label:'I',  cmd:'italic',         style:{ fontStyle:'italic' } },
    { label:'U',  cmd:'underline',      style:{ textDecoration:'underline' } },
    { label:'S',  cmd:'strikeThrough',  style:{ textDecoration:'line-through' } },
    { label:'P',  cmd:'p' },
    { label:'H1', cmd:'h1',             style:{ fontSize:10 } },
    { label:'H2', cmd:'h2',             style:{ fontSize:10 } },
    { label:'H3', cmd:'h3',             style:{ fontSize:10 } },
    { label:'•',  cmd:'insertUnorderedList' },
    { label:'1.', cmd:'insertOrderedList', style:{ fontSize:10 } },
    { label:'"',  cmd:'blockquote' },
    { label:'—',  cmd:'insertHorizontalRule' },
  ];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:2, padding:'6px 10px', borderBottom:'1px solid #E5E7EB', flexWrap:'wrap', background:'#FAFAFA' }}>
      {btns.map(b => (
        <button key={b.cmd}
          onMouseDown={e => { e.preventDefault(); ['h1','h2','h3','p','blockquote'].includes(b.cmd) ? document.execCommand('formatBlock',false,b.cmd) : document.execCommand(b.cmd,false,null); }}
          style={{ padding:'4px 7px', border:'none', background:'transparent', borderRadius:4, fontSize:12, cursor:'pointer', color:'#374151', minWidth:26, ...b.style }}
          onMouseEnter={e=>e.currentTarget.style.background='#E5E7EB'}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          {b.label}
        </button>
      ))}
      <button onMouseDown={e=>e.preventDefault()}
        style={{ padding:'4px 7px', border:'none', background:'transparent', borderRadius:4, cursor:'pointer', color:'#374151' }}
        onMouseEnter={e=>e.currentTarget.style.background='#E5E7EB'}
        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
        <Icon name="rows" size={13} />
      </button>
    </div>
  );
}

// ── Note Card ─────────────────────────────────────────────────────────────────
function NoteCard({ note, noteFiles, isConverted, onEdit, onDelete, currentUser, openMail, onPreviewFile, clientId }) {
  const [showMenu, setShowMenu] = useState(false);
  const name   = note.createdBy?.name || currentUser?.name || 'User';
  const color  = avatarColor(name);

  useEffect(() => {
    if (!showMenu) return;
    const close = () => setShowMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showMenu]);

  const actionBtns = [
    { title:'Reply',    icon:'reply' },
{ title:'Forward', icon:'forward', onClick: () => openMail(note) },
    { title:'Edit',     icon:'edit',     onClick:() => onEdit(note) },
    { title:'Download', icon:'download' },
  ];

  return (
    <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, marginBottom:12, overflow:'hidden' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
            {getInitials(name)}
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:'#1F2937' }}>{name}</span>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          {actionBtns.map(btn => (
            <button key={btn.title} title={btn.title}
              onClick={isConverted && btn.onClick ? btn.onClick : undefined}
              style={{
                width:28, height:28, border:'1px solid', borderRadius:6,
                display:'flex', alignItems:'center', justifyContent:'center',
                cursor: isConverted ? 'pointer' : 'default',
                borderColor: isConverted ? '#E5E7EB' : 'transparent',
                background:  isConverted ? '#fff' : 'transparent',
                color:       isConverted ? '#6B7280' : '#D1D5DB',
                transition:'all 0.15s',
              }}
              onMouseEnter={e=>{ if(isConverted){ e.currentTarget.style.borderColor='#976793'; e.currentTarget.style.color='#976793'; }}}
              onMouseLeave={e=>{ if(isConverted){ e.currentTarget.style.borderColor='#E5E7EB'; e.currentTarget.style.color='#6B7280'; }}}>
              <Icon name={btn.icon} size={13} />
            </button>
          ))}

          {/* More button */}
          <div style={{ position:'relative' }}>
            <button
              onClick={isConverted ? e => { e.stopPropagation(); setShowMenu(m=>!m); } : undefined}
              style={{ width:28, height:28, border:'1px solid', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', cursor:isConverted?'pointer':'default', borderColor:isConverted?'#E5E7EB':'transparent', background:isConverted?'#fff':'transparent', color:isConverted?'#6B7280':'#D1D5DB' }}>
              <Icon name="more" size={13} />
            </button>
            {showMenu && isConverted && (
              <div style={{ position:'absolute', right:0, top:32, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, boxShadow:'0 4px 20px rgba(0,0,0,0.1)', zIndex:200, minWidth:130, overflow:'hidden' }}
                onClick={e=>e.stopPropagation()}>
                {['Edit','Delete'].map(action => (
                  <button key={action}
                    onClick={() => { if(action==='Edit') onEdit(note); if(action==='Delete') onDelete(note._id); setShowMenu(false); }}
                    style={{ display:'block', width:'100%', textAlign:'left', padding:'9px 14px', border:'none', background:'transparent', fontSize:13, cursor:'pointer', color:action==='Delete'?'#EF4444':'#374151' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    {action}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:'14px 16px' }}>
        <div style={{ paddingLeft:16, borderLeft:'3px solid #E5E7EB', fontSize:13, color:'#6B7280', lineHeight:1.65 }}
          dangerouslySetInnerHTML={{ __html: note.description || '' }} />
          <div style={{ marginTop: 10 }}>
            {noteFiles?.[note._id]?.map(file => (
  <img
    key={file._id}
    src={resolveFileUrl(file.url, { clientId })}
    alt={file.title || file.filename || 'attachment'}
    style={{
      width: 120,
      height: 90,
      objectFit: 'cover',
      borderRadius: 6,
      marginTop: 8,
      marginRight: 6,
      cursor: 'pointer',
      border: '1px solid #E5E7EB',
    }}
    onClick={() => onPreviewFile?.(file)}
  />
))}
  {note.cc?.length > 0 && (
    <div style={{ fontSize: 12, color: '#6B7280' }}>
      <strong>cc:</strong>
      {note.cc.map(user => (
        <span key={user._id} style={{
          marginLeft: 6,
          padding: '2px 8px',
          background: '#F7F1F7',
          borderRadius: 20,
          fontSize: 11,
          color: '#976793'
        }}>
          {user.name}
        </span>
      ))}
    </div>
  )}
</div>
        <div style={{ fontSize:11, color:'#9CA3AF', marginTop:10 }}>{new Date(note.createdAt).toLocaleString()}</div>
      </div>
    </div>
  );
}

// ── Files Tab ──────────────────────────────────────────────────────────────────
function FilesTab({ isConverted, clientId }) {
  const [previewFile, setPreviewFile] = useState(null);
  const [activeFileTab, setActiveFileTab] = useState('recce');
  const [files, setFiles] = useState([]);
  const [noteFiles, setNoteFiles] = useState({});
  
  const [openMenu, setOpenMenu] = useState(null);

  const handleDownload = async (file) => {
  try {
    const token = localStorage.getItem("token"); // or wherever you store it
    const targetUrl = resolveFileUrl(file.url, { clientId });

    const headers = {};
    if (token && !targetUrl.startsWith("https://res.cloudinary.com")) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(targetUrl, { headers });

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = file.filename;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

  } catch (err) {
    console.error(err);
    alert("Download failed");
  }
};
 
  const handleDelete = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;

    try {
      await api.delete(`/clients/${clientId}/files/${fileId}`);
      fetchFiles(); // refresh
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  useEffect(() => {
  const handleClick = () => setOpenMenu(null);
  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, []);

useEffect(() => {
  fetchFiles();
}, [clientId, activeFileTab]);

const fetchFiles = async () => {
  try {
    const res = await api.get(
      `/clients/${clientId}/files`,
      {
        params: { fileType: activeFileTab }
      }
    );

    setFiles(res.data.data.files);
  } catch (err) {
    console.error(err);
  }
};
  const [viewGrid, setViewGrid]           = useState(false);
  const fileRef = useRef();

 const handleFiles = async (files) => {
  const fileArray = Array.from(files);

  try {
    for (let file of fileArray) {

      console.log("DEBUG:", {
        fileName: file.name,
        activeFileTab
      });

      const formData = new FormData();

      formData.append('file', file);
      formData.append('title', file.name || 'Untitled');
      formData.append('fileType', activeFileTab || 'recce');

await api.post(
  `/clients/${clientId}/files`,
  formData
);

}

    alert('Upload done');
    fetchFiles(); // ✅ VERY IMPORTANT (refresh UI)

  } catch (err) {
    console.error('STEP ERROR:', err);
    alert('Upload failed');
  }
};

     
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ display:'flex', gap:4 }}>
          {FILE_CATS.map(cat => (
  <button key={cat.key} onClick={() => setActiveFileTab(cat.key)}>
    {cat.label}
  </button>
))}

        </div>


        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button
onClick={() => fileRef.current?.click()}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 16px', background:'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', transition:'background 0.15s' }}>
            <Icon name="upload" size={14} />
            Upload Files
          </button>
<input
  ref={fileRef}
  type="file"
  multiple
  style={{ display:'none' }}
  onChange={(e) => handleFiles(e.target.files)}
/>          <button onClick={()=>setViewGrid(true)}  style={{ width:30, height:30, border:'1px solid #E5E7EB', borderRadius:6, background:viewGrid?'#F7F1F7':'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:viewGrid?'#976793':'#9CA3AF' }}><Icon name="grid" size={14} /></button>
          <button onClick={()=>setViewGrid(false)} style={{ width:30, height:30, border:'1px solid #E5E7EB', borderRadius:6, background:!viewGrid?'#F7F1F7':'#fff', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:!viewGrid?'#976793':'#9CA3AF' }}><Icon name="rows" size={14} /></button>
        </div>
      </div>
      <div className="files-grid">
  {files.length === 0 ? (
<div style={{
  textAlign: 'center',
  padding: '40px',
  color: '#9CA3AF',
  fontSize: 14
}}>
  📁 No files uploaded yet
</div>

  ) : (
    files.map((file) => (
      <div key={file._id} className="file-card">

        {/* Title */}
        <div className="file-title">
  {file.title}

  <div style={{ position: 'relative' }}>
    <button
      onClick={(e) => {
        e.stopPropagation();
        setOpenMenu(openMenu === file._id ? null : file._id);
      }}
      style={{
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 16
      }}
    >
      ⋮
    </button>

    {openMenu === file._id && (
      <div className="file-menu">

        <button onClick={() => handleDownload(file)}>
          Download
        </button>

        <button
          onClick={() => handleDelete(file._id)}
          style={{ color: 'red' }}
        >
          Delete
        </button>

      </div>
    )}
  </div>
  
</div>

        {/* Image */}
        <img
  src={`http://localhost:5000${file.url}`}
  alt={file.title}
  className="file-image"
  onClick={(e) => {
    e.stopPropagation();
    setPreviewFile(file);
  }}
/>

        {/* Info */}
        <div className="file-info">
          <div className="file-name">{file.filename}</div>
          <div className="file-meta">
            {new Date(file.createdAt).toLocaleString()}
          </div>
        </div>

      </div>
    ))
  )}
</div>
{previewFile && (
  <div
    onClick={() => setPreviewFile(null)}
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.9)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999
    }}
  >
    <button
      onClick={() => setPreviewFile(null)}
      style={{
        position: "absolute",
        top: 20,
        right: 30,
        fontSize: 24,
        color: "#fff",
        background: "transparent",
        border: "none",
        cursor: "pointer"
      }}
    >
      ✕
    </button>

    <img
      src={`http://localhost:5000${previewFile.url}`}
      style={{
        maxWidth: "90%",
        maxHeight: "90%",
        borderRadius: 12
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </div>
)}
    </div>
    
    
  );
}

// ── Notes Tab ─────────────────────────────────────────────────────────────────
function NotesTab({ clientId, isConverted, user }) {
  const editorRef   = useRef();
  const fileRef     = useRef();
  const [notes, setNotes]           = useState([]);
  const [mailModal, setMailModal] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading]       = useState(true);
const [cc, setCc] = useState([]);       // array for multiple users
const [showCC, setShowCC] = useState(false);  const [users, setUsers] = useState([]);
  const [files, setFiles]           = useState([]);
  const [saving, setSaving]         = useState(false);
  const [noteFiles, setNoteFiles] = useState({});

  const loadFiles = async (noteId) => {
  if (!noteId) return []; // 🔥 IMPORTANT

  try {
    const res = await getFilesByLink(clientId, "note", noteId);
    return res.data.data.files;
  } catch (err) {
    console.error("File fetch failed for note:", noteId);
    return []; // 🔥 prevent crash
  }
};

  const [editingNote, setEditingNote] = useState(null);
  const [noteSearch, setNoteSearch] = useState('');

  const loadNotes = useCallback(async () => {
    try {
      const r = await api.get('/activity-logs', { params:{ client: clientId, type:'note', limit:100 } });
      setNotes(r.data.data.logs || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  useEffect(() => {
  if (!notes.length) return;

  let isMounted = true;

  const fetchFiles = async () => {
    try {
      const results = await Promise.all(
        notes.map(async (note) => {
          if (!note?._id) return null;

          const files = await loadFiles(note._id);
          return { noteId: note._id, files };
        })
      );

      if (!isMounted) return;

      const map = {};
      results.forEach(r => {
        if (r) map[r.noteId] = r.files;
      });

      setNoteFiles(map);

    } catch (err) {
      console.error(err);
    }
  };

  fetchFiles();

  return () => {
    isMounted = false;
  };

}, [notes]);
  useEffect(() => {
  const close = () => setShowCC(false);
  window.addEventListener("click", close);
  return () => window.removeEventListener("click", close);
}, []);

  useEffect(() => {
  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      console.log("USERS API:", res.data);

// handle different response formats
const userData =
  Array.isArray(res.data) ? res.data :
  Array.isArray(res.data.data) ? res.data.data :
  Array.isArray(res.data.data?.users) ? res.data.data.users :
  [];

setUsers(userData);
    } catch (err) {
      console.error(err);
    }
  };

  fetchUsers();
}, []);

  const handleSubmit = async () => {
  const body = editorRef.current?.innerHTML || '';

  if (!body.trim()) {
    alert("Note cannot be empty");
    return;
  }

  setSaving(true);

  try {
    if (editingNote) {
      await api.put(`/activity-logs/${editingNote._id}`, {
        description: body,
        type: 'note'
      });
    } else {
      const res = await api.post('/activity-logs', {
  description: body,
  type: 'note',
  client: clientId,
  cc
});

const noteId = res.data.data.log._id;

// 🔥 UPLOAD FILES AFTER NOTE
if (files.length > 0) {
  for (let file of files) {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("title", file.name);
    formData.append("fileType", "recce");

    formData.append("linkedTo", "note");
    formData.append("linkedId", noteId);

    await uploadFiles(clientId, formData);
  }
}
    }

    // reset
    if (editorRef.current) editorRef.current.innerHTML = '';
    setCc([]);
    setFiles([]);
    setEditingNote(null);

    await loadNotes();

  } catch (err) {
    console.error(err);
    alert("Error saving note");
  }

  setSaving(false);
};

  const handleEdit = (note) => {
    setEditingNote(note);
setCc([]);
    if (editorRef.current) editorRef.current.innerHTML = note.description || '';
    window.scrollTo({ top:0, behavior:'smooth' });
  };

  const openMail = (note) => {
  setMailModal(note);
};

  const startListening = () => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  if (!SpeechRecognition) {
    alert("Speech recognition not supported");
    return;
  }

  const recognition = new SpeechRecognition();
  recognition.lang = "en-IN";

  recognition.onresult = (event) => {
    const text = event.results[0][0].transcript;

    if (editorRef.current) {
      editorRef.current.innerHTML += " " + text;
    }
  };

  recognition.start();
};

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await api.delete(`/activity-logs/${id}`);
    loadNotes();
  };

  const filtered = notes.filter(n =>
  !noteSearch ||
  n.description?.toLowerCase().includes(noteSearch.toLowerCase())
);

  return (
    <div style={{ maxWidth:780, margin:'0 auto' }}>
      {/* Editor */}
      <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden', marginBottom:28, boxShadow:'0 1px 6px rgba(0,0,0,0.05)' }}>
        
        <RichToolbar />
        <div ref={editorRef} contentEditable suppressContentEditableWarning
          style={{ minHeight:90, padding:'14px 16px', fontSize:13, color:'#374151', outline:'none', lineHeight:1.7 }} />
        {/* Voice btn */}
        <div style={{ display:'flex', justifyContent:'flex-end', padding:'4px 16px 10px' }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
<div
  onClick={startListening}
  style={{
    width:36,
    height:36,
    borderRadius:'50%',
    background:'#6366F1',
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    cursor:'pointer'
  }}
>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" fill="none" stroke="white" strokeWidth="2"/></svg>
            </div>
            <span style={{ fontSize:10, color:'#9CA3AF' }}>00:00</span>
          </div>
        </div>
        {/* CC */}
        <div style={{ marginTop: 10, position: "relative", width: "100%" }}>

  <div style={{ fontWeight: 600, marginBottom: 6 }}>CC</div>

  <div
    onClick={(e) => {
      e.stopPropagation();
      setShowCC(!showCC);
    }}
    style={{
      width: "100%",
      border: "1px solid #D1D5DB",
      borderRadius: 8,
      padding: "10px 12px",
      background: "#F9FAFB",
      cursor: "pointer",
      fontSize: 13
    }}
  >
    {cc.length === 0
      ? "Select users..."
      : `${cc.length} selected`}
  </div>

  {showCC && (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: "110%",
        left: 0,
        width: "100%",
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        maxHeight: 220,
        overflowY: "auto",
        zIndex: 9999
      }}
    >
      {Array.isArray(users) && users.map((user) => (
        <div
          key={user._id}
          style={{
            padding: "10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 10
          }}
        >
          <input
            type="checkbox"
            checked={cc.includes(user._id)}
            onChange={(e) => {
              if (e.target.checked) {
                setCc([...cc, user._id]);
              } else {
                setCc(cc.filter((id) => id !== user._id));
              }
            }}
          />
          <span>{user.name}</span>
        </div>
      ))}
    </div>
  )}
</div>
        {/* Files */}
        <div style={{ borderTop:'1px solid #E5E7EB', padding:'10px 16px' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:5 }}>Files</div>
          <div style={{ fontSize:12, color:'#9CA3AF', marginBottom:8 }}>{files.length ? files.map(f=>f.name).join(', ') : 'No files selected'}</div>
          <button onClick={()=>fileRef.current?.click()}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', background:'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:600, cursor:'pointer' }}>
            <Icon name="upload" size={13} /> Upload
          </button>
          <input ref={fileRef} type="file" multiple style={{ display:'none' }} onChange={e=>setFiles(Array.from(e.target.files))} />
        </div>
        {/* Submit */}
        <div style={{ borderTop:'1px solid #E5E7EB', padding:'10px 16px', display:'flex', justifyContent:'flex-end', gap:8 }}>
          {editingNote && (
            <button onClick={()=>{ setEditingNote(null); setCc([]); if(editorRef.current) editorRef.current.innerHTML=''; }}
              style={{ padding:'7px 18px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', fontSize:13, cursor:'pointer', color:'#374151' }}>Cancel</button>
          )}
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding:'7px 20px', background:saving?'#9CA3AF':'#976793', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving…' : editingNote ? 'Update' : 'Submit'}
          </button>
        </div>
      </div>

      {/* All Notes */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:14 }}>
        <h3 style={{ fontSize:16, fontWeight:700, color:'#976793', margin:0 }}>All notes</h3>
        <button onClick={loadNotes} style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF' }}><Icon name="refresh" size={14} /></button>
      </div>
      <div style={{ position:'relative', marginBottom:14 }}>
        <svg style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', pointerEvents:'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
        <input placeholder="Search notes…" value={noteSearch} onChange={e=>setNoteSearch(e.target.value)}
          style={{ width:'100%', padding:'8px 12px 8px 32px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'#fff', fontFamily:'inherit' }} />
      </div>
      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF', fontSize:14, border:'1px solid #E5E7EB', borderRadius:10 }}>
          No notes yet. Add your first note above.
        </div>
      ) : filtered.map(note => (
<NoteCard
  key={note._id}
  note={note}
  noteFiles={noteFiles}
  isConverted={isConverted}
  onEdit={handleEdit}
  onDelete={handleDelete}
  currentUser={user}
  openMail={openMail}
  onPreviewFile={(file) => setPreviewFile(file)}
  clientId={clientId}
/>      ))}
{previewFile && (
  <div
    onClick={() => setPreviewFile(null)}
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "rgba(0,0,0,0.9)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000
    }}
  >
    <button
      onClick={() => setPreviewFile(null)}
      style={{
        position: "absolute",
        top: 20,
        right: 30,
        fontSize: 24,
        color: "#fff",
        background: "transparent",
        border: "none",
        cursor: "pointer"
      }}
    >
      ✕
    </button>

    <img
      src={resolveFileUrl(previewFile.url, { clientId })}
      alt={previewFile.title || previewFile.filename || 'attachment'}
      style={{
        maxWidth: "92%",
        maxHeight: "92%",
        borderRadius: 12,
        objectFit: "contain"
      }}
      onClick={(e) => e.stopPropagation()}
    />
  </div>
)}
{mailModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  }}>
    <div style={{
      width: 500,
      background: '#fff',
      borderRadius: 12,
      padding: 20,
      boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
    }}>

      <h3 style={{ marginBottom: 16 }}>Send Mail</h3>

      {/* TO */}
      <label style={{ fontSize: 12, color: '#6B7280' }}>To</label>
      <input
        placeholder="Enter email"
        style={{
          width: '100%',
          padding: 10,
          marginBottom: 12,
          border: '1px solid #E5E7EB',
          borderRadius: 8
        }}
      />

      {/* CC */}
      <label style={{ fontSize: 12, color: '#6B7280' }}>CC</label>
      <div style={{
        padding: 10,
        marginBottom: 12,
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        background: '#F9FAFB'
      }}>
        {mailModal.cc?.length > 0
          ? mailModal.cc.map(u => u.name).join(', ')
          : 'No CC users'}
      </div>

      {/* SUBJECT */}
      <label style={{ fontSize: 12, color: '#6B7280' }}>Subject</label>
      <input
        defaultValue={mailModal.title || 'Note'}
        style={{
          width: '100%',
          padding: 10,
          marginBottom: 12,
          border: '1px solid #E5E7EB',
          borderRadius: 8
        }}
      />

      {/* BODY */}
      <label style={{ fontSize: 12, color: '#6B7280' }}>Message</label>
      <textarea
        defaultValue={mailModal.description}
        style={{
          width: '100%',
          height: 120,
          padding: 10,
          border: '1px solid #E5E7EB',
          borderRadius: 8
        }}
      />

      {/* BUTTONS */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
        <button
          onClick={() => setMailModal(null)}
          style={{
            padding: '8px 16px',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>

        <button
          style={{
            padding: '8px 16px',
            background: '#976793',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer'
          }}
        >
          Send
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}

// ── Coming Soon placeholder ───────────────────────────────────────────────────
function ComingSoon({ label }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'80px 0', color:'#9CA3AF', gap:12 }}>
      <Icon name="doc" size={40} />
      <p style={{ fontSize:15, fontWeight:500 }}>{label} — coming soon</p>
    </div>
  );
}

// ── Main ClientDetail ─────────────────────────────────────────────────────────
export default function ClientDetail() {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromProject     = searchParams.get('from') === 'project';
  const { user }        = useAuth();

  const [client, setClient]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [isConverted, setIsConverted] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showInfo, setShowInfo]     = useState(false);
  const [mainStage, setMainStage]   = useState('Sales');
  const [subStage, setSubStage]     = useState('Quotation Sent');

  const loadClient = useCallback(async () => {
    try {
      const r = await getClientApi(id);
      const c = r.data.data.client;
      setClient(c);
      // "active" status = converted
      setIsConverted(c.status === 'active');
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadClient(); }, [loadClient]);

  // Close info on outside click
  useEffect(() => {
    if (!showInfo) return;
    const close = () => setShowInfo(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showInfo]);

  const rawClientTab = searchParams.get('tab') || 'files';
  const activeTab = getEffectiveClientTab(rawClientTab, isConverted);

  useEffect(() => {
    if (!client) return;
    const t = searchParams.get('tab') || 'files';
    const meta = VISIBLE_CLIENT_TABS.find((v) => v.id === t);
    if (meta?.locked && !isConverted) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'files');
          return next;
        },
        { replace: true }
      );
    }
  }, [client, isConverted, searchParams, setSearchParams]);

  const handleToggleConvert = async () => {
    if (converting) return;
    setConverting(true);
    const newStatus = isConverted ? 'prospect' : 'active';
    try {
      await updateClientApi(id, { status: newStatus });
      setIsConverted(!isConverted);
      setClient(c => c ? ({ ...c, status: newStatus }) : c);
    } catch(e) { console.error(e); }
    finally { setConverting(false); }
  };

  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
        <div style={{ width:36, height:36, border:'3px solid #E5E7EB', borderTopColor:'#976793', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#fff', fontFamily:'inherit' }}>

      {/* ── Page Header ── */}
      <div style={{ padding:'14px 24px', borderBottom:'1px solid #E5E7EB', background:'#fff' }}>
        {/* Back */}
        <button onClick={() => navigate(fromProject ? '/projects' : '/clients')}
          style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none', color:'#9CA3AF', fontSize:12, cursor:'pointer', marginBottom:12, padding:0, fontFamily:'inherit' }}>
          <Icon name="back" size={13} />
          {fromProject ? 'Back to Projects' : 'Back to Clients'}
        </button>

        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          {/* Left — name + controls */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              {/* Name */}
              <h2 style={{ fontSize:20, fontWeight:700, color:'#1F2937', margin:0 }}>{client?.name}</h2>

              {/* Edit */}
              <button style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', padding:0 }}>
                <Icon name="edit" size={15} />
              </button>

              {/* Project code badge */}
              <span style={{ background:'#F7F1F7', color:'#976793', padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:700 }}>
                C-{id?.slice(-4)?.toUpperCase()}
              </span>

              {/* ── Conversion Toggle ── */}
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {/* If converted — show green checkmark; else show grey toggle */}
                {isConverted ? (
                  <button onClick={handleToggleConvert} title="Click to un-convert client"
                    style={{ width:22, height:22, borderRadius:'50%', border:'none', background:'#10B981', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all 0.2s' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                ) : (
                  <div onClick={handleToggleConvert} title="Toggle to convert client"
                    style={{ width:36, height:20, borderRadius:10, background:'#D1D5DB', cursor:converting?'wait':'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                    <div style={{ position:'absolute', top:2, left:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }} />
                  </div>
                )}
              </div>

              {/* Info button */}
              <div style={{ position:'relative' }}>
                <button onClick={e=>{ e.stopPropagation(); setShowInfo(s=>!s); }}
                  style={{ width:22, height:22, borderRadius:'50%', border:'1.5px solid #3B82F6', background:'#F7F1F7', color:'#3B82F6', fontSize:12, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' }}>i</button>
                {showInfo && client && <InfoTooltip client={client} />}
              </div>
            </div>

            {/* Client name link + phone */}
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <button style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none', color:'#976793', fontSize:13, fontWeight:500, cursor:'pointer', padding:0, fontFamily:'inherit' }}>
                <Icon name="user" size={13} />{client?.name}
              </button>
              {client?.phone && (
                <button style={{ display:'flex', alignItems:'center', gap:5, background:'none', border:'none', color:'#374151', fontSize:13, cursor:'pointer', padding:0, fontFamily:'inherit' }}>
                  <Icon name="phone" size={13} />{client.phone}
                </button>
              )}
            </div>
          </div>

          {/* Right — Stage selectors */}
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
                Main Stage
                <div title="Main pipeline stage" style={{ width:14, height:14, borderRadius:'50%', border:'1px solid #9CA3AF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#9CA3AF', cursor:'help' }}>?</div>
              </div>
              <select value={mainStage} onChange={e=>setMainStage(e.target.value)}
                style={{ padding:'7px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'#fff', minWidth:110, fontFamily:'inherit', cursor:'pointer' }}>
                {['Sales','Design','Execution','Handover'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:'#9CA3AF', marginBottom:4 }}>Sub Stage</div>
              <select value={subStage} onChange={e=>setSubStage(e.target.value)}
                style={{ padding:'7px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'#fff', minWidth:155, fontFamily:'inherit', cursor:'pointer' }}>
                {['Quotation Sent','Lead Received','In Progress','Unqualified','Call Not Received','Site Visit Schedule','Lost'].map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={{ borderBottom:'1px solid #E5E7EB', background:'#fff', overflowX:'auto', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', paddingLeft:24, minWidth:'max-content' }}>
          {VISIBLE_CLIENT_TABS.map(tab => {
            const disabled = tab.locked && !isConverted;
            const active   = activeTab === tab.id;
            return (
              <button key={tab.id}
                onClick={() => {
                  if (disabled) return;
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('tab', tab.id);
                      return next;
                    },
                    { replace: true }
                  );
                }}
                title={disabled ? 'Convert client to unlock this tab' : tab.label}
                style={{
                  display:'flex', alignItems:'center', gap:5,
                  padding:'10px 14px', fontSize:13,
                  fontWeight: active ? 600 : 400,
                  color: disabled ? '#C4C9D4' : active ? '#976793' : '#6B7280',
                  background:'transparent', border:'none',
                  borderBottom: active ? '2px solid #976793' : '2px solid transparent',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  whiteSpace:'nowrap', marginBottom:-1,
                  transition:'all 0.15s',
                  opacity: disabled ? 0.5 : 1,
                }}>
                <Icon name={tab.icon} size={12} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', background:'#F8F9FB' }}>
        {activeTab === 'files'       && <FilesTab clientId={id} isConverted={isConverted} />}
        {activeTab === 'notes'   && <NotesTab clientId={id} isConverted={isConverted} user={user} />}
        {activeTab === 'tasks'   && <ComingSoon label="Tasks" />}
        {activeTab === 'orders'      && <OrdersTab clientId={id} />}
        {activeTab === 'financials'  && <FinancialsTab clientId={id} />}
        {activeTab === 'invoices' && <InvoicesTab clientId={id} client={client} />}
        {activeTab === 'inventory' && <ClientInventoryTab clientId={id} isConverted={isConverted} />}



{activeTab === 'quotes' && (
  <QuotesTab 
    clientId={id} 
    projectId={null} 
    isConverted={isConverted} 
  />
)}        {activeTab === 'moodboard'&& <ComingSoon label="Moodboard" />}
        {activeTab === 'details' && client && (
  <div style={{ maxWidth:900, fontFamily:'inherit' }}>

    {/* Client Details (renamed from Contact Details) */}
    {[
      { title:'Client Details', fields:[['Name',client.name],['Email',client.email||'N/A'],['Phone',client.phone||'N/A']] },
      { title:'Project Details', fields:[['Created on',new Date(client.createdAt).toLocaleDateString('en-IN')],['Last updated on',new Date(client.updatedAt).toLocaleDateString('en-IN')],['Budget','#'],['Description','N/A']] },
      { title:'Address Details', fields:[['Address line 1',client.address?.street||'Nibm'],['Address line 2','N/A'],['City',client.address?.city||'N/A'],['State',client.address?.state||'Maharashtra'],['Pincode',client.address?.pincode||'N/A'],['Location','N/A']] },
      { title:'Other Details', fields:[['Legal name',client.company||'N/A'],['GST number','N/A']] },
    ].map(section=>(
      <div key={section.title} style={{ background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',marginBottom:16 }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderBottom:'1px solid #E5E7EB' }}>
          <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>{section.title}</span>
          <button style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,cursor:'pointer',color:'#374151' }}>
            ✏ Edit
          </button>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:0,padding:'14px 20px' }}>
          {section.fields.map(([label,val])=>(
            <div key={label} style={{ padding:'6px 0' }}>
              <div style={{ fontSize:11,color:'#9CA3AF',marginBottom:2 }}>{label}</div>
              <div style={{ fontSize:13,color:'#374151',fontWeight:500 }}>{val||'N/A'}</div>
            </div>
          ))}
        </div>
      </div>
    ))}

    {/* Documents */}
    <div style={{ background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',marginBottom:16 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderBottom:'1px solid #E5E7EB' }}>
        <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>Documents</span>
        <button style={{ background:'none',border:'none',color:'#976793',fontSize:12,cursor:'pointer',fontWeight:500 }}>+ Add Documents</button>
      </div>
      <div style={{ padding:'16px 20px',color:'#9CA3AF',fontSize:13 }}>No documents found for this project.</div>
    </div>

    {/* Additional Fields */}
    <div style={{ background:'#fff',border:'1px solid #E5E7EB',borderRadius:12,overflow:'hidden',marginBottom:16 }}>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderBottom:'1px solid #E5E7EB' }}>
        <span style={{ fontSize:13,fontWeight:700,color:'#1F2937' }}>Additional Fields</span>
        <button style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 12px',border:'1px solid #E5E7EB',borderRadius:8,background:'#fff',fontSize:12,cursor:'pointer',color:'#374151' }}>✏ Edit</button>
      </div>
      <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:0,padding:'14px 20px' }}>
        {[['Timeline','N/A'],['BHK Type','N/A'],['Lead Status','N/A'],['Timeline','N/A'],['Initial Budget','N/A']].map(([l,v],i)=>(
          <div key={i} style={{ padding:'6px 0' }}>
            <div style={{ fontSize:11,color:'#9CA3AF',marginBottom:2 }}>{l}</div>
            <div style={{ fontSize:13,color:'#374151' }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
        )}
      </div>
    </div>
  );
}